/* eslint-disable */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, of, Subject, throwError } from 'rxjs'
import { catchError, switchMap, tap } from 'rxjs/operators'
import { Router } from '@angular/router'

import { JwtService } from '@app/modules/auth/jwt.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { LogoutResponse } from '@app/modules/auth/auth-response.model'
import { ApiService } from '@shared/services/api.service'
import { SKIP_ERROR_PAGE_REDIRECT } from './error-redirect-context'
import { safeHttpUrl } from '@core/helpers/safe-url'

import camelCase from 'lodash-es/camelCase'
import isObject from 'lodash-es/isObject'
import isPlainObject from 'lodash-es/isPlainObject'
import isArray from 'lodash-es/isArray'

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false

  private tokenRefreshed = new Subject<void>()
  private readonly tokenRefreshed$ = this.tokenRefreshed.asObservable()

  constructor(
    private jwtService: JwtService,
    private authService: AuthService,
    private router: Router,
    private readonly apiService: ApiService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Keep caller headers separate from the bearer added for this attempt. A retry must
    // read the renewed token, while explicit caller Authorization remains untouched.
    const originalRequest = request
    request = this.addAuthHeader(originalRequest)

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          if (event.body && event.body.data) {
            event.body.data = this.objectToCamelCase(event.body.data)
          } else if (event.body && typeof event.body !== 'string') {
            event.body.data = this.objectToCamelCase(event.body)
          }
        }
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          // Never let the auth endpoints themselves re-enter the refresh branch. A 401 from
          // /token/refresh (expired refresh token) must propagate so the outer catchError logs out,
          // otherwise refreshToken() re-triggers while refreshTokenInProgress is true and hangs
          // forever waiting on tokenRefreshed$ (blank page, no redirect).
          const url = error.url || ''
          const isAuthEndpoint = url.includes('logout') || url.includes('token/refresh') || /\/(client|user|reseller)\/\d+\/login/.test(url)
          if (error.status === 401 && !isAuthEndpoint && this.jwtService.getToken()) {
            // L30-1826: a "login as" tab has no refresh token. Its token can be invalidated
            // underneath it (another admin logging in as the same client pushes it out of the
            // target user's 5-session cap), so re-mint the impersonation with the admin's own
            // token and retry, instead of dropping the whole tab to the login page.
            if (this.authService.isImpersonationTab()) {
              return this.reissueImpersonation().pipe(
                switchMap(() => {
                  request = this.addAuthHeader(originalRequest)
                  return next.handle(request)
                }),
                catchError(() => {
                  this.logout()
                  return of()
                }),
              )
            }
            if (this.jwtService.getRefreshToken()) {
              return this.refreshToken().pipe(
                switchMap(() => {
                  request = this.addAuthHeader(originalRequest)
                  return next.handle(request)
                }),
                catchError(() => {
                  this.logout()
                  return of()
                }),
              )
            } else {
              this.logout()
            }
          }

          // Best-effort callers (vd the score-band table fetch) opt out of the redirect via
          // SKIP_ERROR_PAGE_REDIRECT; the error still reaches their catchError below.
          if (error.status === 500 && !request.context.get(SKIP_ERROR_PAGE_REDIRECT)) {
            this.router.navigate(['error-page'])
          }
        }

        return throwError(error)
      }),
    )
  }

  logout() {
    // Snapshot the token we are logging out. The server /logout call is best-effort — purge and
    // redirect must happen even if it errors (an expired token can make /logout 401 too).
    const tokenAtLogout = this.jwtService.getToken()

    const finish = (response?: LogoutResponse) => {
      const currentToken = this.jwtService.getToken()
      if (currentToken && currentToken !== tokenAtLogout) {
        // A new token was stored (user re-authenticated / impersonation) before this logout
        // completed — do not disrupt the new session.
        return
      }
      this.authService.purgeAuth()
      // Only follow a server-supplied redirect when it is a safe http(s)/relative target so a
      // reflected javascript:/data:/protocol-relative value cannot turn logout into an open redirect.
      const safeRedirect = safeHttpUrl(response?.redirectUrl)
      safeRedirect ? window.open(safeRedirect, '_self') : void this.router.navigate(['login'])
    }

    this.authService.logout().subscribe({
      next: (response) => finish(response),
      error: () => finish(),
    })
  }

  addAuthHeader(request: HttpRequest<any>) {
    const isApiUrl = request.url.startsWith(this.apiService.baseUrl) || request.url.startsWith(this.apiService.goBaseUrl)
    // A caller that set Authorization itself (reissueImpersonation sends the admin's token from a
    // tab whose current token is the impersonated user's) keeps it.
    if (isApiUrl && this.jwtService.getToken() && !request.headers.has('Authorization')) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${this.jwtService.getToken()}`,
        },
      })
    }
    return request
  }

  // Same single-flight shape as refreshToken(): concurrent 401s in a "login as" tab wait for the
  // one re-issue in progress instead of each minting a session (which is the very churn that
  // evicts sessions, L30-1826).
  private reissueImpersonation() {
    if (this.refreshTokenInProgress) {
      return new Observable((observer) => {
        this.tokenRefreshed$.subscribe(() => {
          observer.next(null)
          observer.complete()
        })
      })
    }
    this.refreshTokenInProgress = true
    return this.authService.reissueImpersonation().pipe(
      tap(() => {
        this.refreshTokenInProgress = false
        this.tokenRefreshed.next()
      }),
      catchError((error) => {
        this.refreshTokenInProgress = false
        return throwError(error)
      }),
    )
  }

  refreshToken() {
    if (this.refreshTokenInProgress) {
      return new Observable((observer) => {
        this.tokenRefreshed$.subscribe(() => {
          observer.next(null)
          observer.complete()
        })
      })
    } else {
      this.refreshTokenInProgress = true

      return this.authService.refreshToken(this.jwtService.getRefreshToken()).pipe(
        tap(() => {
          this.refreshTokenInProgress = false
          this.tokenRefreshed.next()
        }),
        catchError((error) => {
          // Clear the flag so a failed refresh doesn't poison later 401s in the session.
          this.refreshTokenInProgress = false
          return throwError(error)
        }),
      )
    }
  }

  private objectToCamelCase(data: any) {
    if (isArray(data)) {
      return data.map((object) => this.objectToCamelCase(object))
    }

    if (isPlainObject(data)) {
      const result = {}

      Object.keys(data).forEach((key) => {
        const value = data[key]
        result[camelCase(key)] = isObject(value) ? this.objectToCamelCase(value) : value
      })

      return result
    }

    return data
  }
}
